import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { take, map, tap } from 'rxjs/operators';
import { QuoteService } from '@core/services/quote.service';
import { ProductsService } from '../services/products.service';
import { TaskService } from '../services/task.service';
import { TaskUtils } from '../store/entities/task/utils/task.util';
import { SessionService } from '@core/services/session.service';
import { ProductModel } from '@entities/product/product.model';
import { TaskModel } from '@entities/task/task.model';
import { VersionedQuoteService } from '@entities/versioned-quote/versioned-quote.service';
import { DsmService } from '@entities/dsm/dsm.service';

@Injectable({
  providedIn: 'root',
})
export class HubPageGuard {
  constructor(
    private quoteService: QuoteService,
    private productsService: ProductsService,
    private taskService: TaskService,
    private sessionService: SessionService,
    private versionedQuoteService: VersionedQuoteService,
    private dsmService: DsmService
  ) {}

  canActivate(): Observable<boolean> {
    return this.checkStore();
  }

  private checkStore(): Observable<boolean> {
    this.sessionService.dispatchHubLoaded();
    return combineLatest([
      this.taskService.getIncompleteTasks(),
      this.productsService.getSelectedProducts(),
      this.versionedQuoteService.isProtectorVersionAccepted(),
      this.versionedQuoteService.isProtectorVersionDeclined(),
      this.sessionService.getIsRetrieve(),
      this.dsmService.waitForDsmCallsIdle(),
      this.sessionService.getIsSpinOff(),
    ]).pipe(
      take(1),
      tap(([tasks, products, protectorVersionAccepted, protectorVersionDeclined, isRetrieve, _dsmIdle, isSpinOff]) =>
        this.rateDraftProductsIfTasksPermit(
          tasks, 
          products, 
          protectorVersionAccepted, 
          protectorVersionDeclined, 
          isRetrieve,
          isSpinOff
        )
      ),
      map(() => true)
    );
  }

  private rateDraftProductsIfTasksPermit(
    tasks: TaskModel[],
    products: ProductModel[],
    protectorVersionAccepted: boolean,
    protectorVersionDeclined: boolean,
    isRetrieve: boolean,
    isSpinOff: boolean
  ): void {
    const draftTasks = tasks.filter((t) => t.ratingStatusOrdinal === 'Draft');
    const draftProducts = products.filter((p) => p.quoteStatus === 'Draft');

    if (
      draftTasks.length > 0 || 
      protectorVersionAccepted ||
      protectorVersionDeclined
    ) {
      for (const product of draftProducts) {
        if (
          draftTasks.find((t) =>
            TaskUtils.isProductType(t.productType, product.type)
          )
        ) {
          if (!isRetrieve || isSpinOff) {
            this.versionedQuoteService.setAwaitingVersionChoice(false);
          }
          continue;
        }
        this.quoteService.dispatchRateQuote(product.type, 'quote');
      }
    } else {
      this.quoteService.rateProductsSpinner(draftProducts);
    }
  }
}
